import { BACKEND_URL } from "../lib/backend";
/**
 * NewsletterArchivePage — Public archive of all published newsletters.
 * Route: /newsletters
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, RefreshCw } from "lucide-react";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";

const API = `${BACKEND_URL}/api`;

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

export default function NewsletterArchivePage() {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Newsletter Archive — Travel Intelligence | TourismIntel";
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/newsletters/list`)
      .then((r) => setNewsletters(r.data.newsletters || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Space Grotesk', sans-serif" }}>
      <PublicPageNav active="newsletter" />

      {/* Hero */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8ecee", padding: "48px 24px 36px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Mail style={{ width: 16, height: 16, color: "#006874" }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b7a7d" }}>
              Newsletter Archive
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#191c1d", lineHeight: 1.1 }}>
            TourismIntel Newsletters
          </h1>
          <p style={{ margin: "12px 0 0", fontSize: 16, color: "#6b7a7d", maxWidth: 540, lineHeight: 1.6 }}>
            Weekly AI-curated travel industry intelligence, delivered to your inbox and readable as sent.
          </p>
          <p style={{ margin: "16px 0 0", fontSize: 12, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "#bac9cc" }}>
            {newsletters.length} {newsletters.length === 1 ? "newsletter" : "newsletters"} published
          </p>
        </div>
      </div>

      {/* Grid */}
      <main style={{ maxWidth: 960, margin: "0 auto", padding: "48px 24px 96px" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
            <RefreshCw style={{ width: 24, height: 24, color: "#006874", animation: "spin 1s linear infinite" }} />
          </div>
        ) : newsletters.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#6b7a7d" }}>
            <Mail style={{ width: 40, height: 40, margin: "0 auto 16px", opacity: 0.3 }} />
            <p style={{ fontSize: 18, fontWeight: 600 }}>No newsletters published yet</p>
            <p style={{ fontSize: 14, marginTop: 8 }}>Check back soon.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {newsletters.map((nl) => (
              <Link
                key={nl.id}
                to={`/newsletter/${nl.seo_slug || nl.id}`}
                data-testid={`archive-newsletter-${nl.id}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  textDecoration: "none",
                  padding: "24px",
                  borderRadius: 16,
                  background: "#fff",
                  border: "1px solid #e8ecee",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  boxShadow: "0 2px 8px rgba(25,28,29,0.04)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#006874";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,104,116,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e8ecee";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(25,28,29,0.04)";
                }}
              >
                <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#006874" }}>
                  {fmt(nl.published_at || nl.created_at)}
                </p>
                <h2 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em", color: "#191c1d", lineHeight: 1.35, flexGrow: 1 }}>
                  {nl.title}
                </h2>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "auto" }}>
                  {nl.article_count > 0 && (
                    <span style={{ fontSize: 12, color: "#6b7a7d" }}>{nl.article_count} articles</span>
                  )}
                  {nl.format_type && (
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "#f0f9fa", color: "#006874", fontWeight: 600 }}>
                      {nl.format_type}
                    </span>
                  )}
                </div>
                <span style={{ marginTop: 16, fontSize: 12, fontWeight: 600, color: "#006874", letterSpacing: "-0.01em", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  Read newsletter →
                </span>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <PublicPageFooter />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
